import { Menu, MenuItem, Typography } from '@mui/material';
import type { InvoiceOverview } from '@weft/api-definition';
import { getPrimaryContact } from '@weft/services';
import { INTEGRATION_PROVIDERS, INVOICE_BILLING_STATUSES, type IntegrationProviders, type InvoiceBillingStatus } from '@weft/types';
import { isEmptyArray, isNotEmptyArray, isNotNullOrUndefined, isNullOrUndefined } from '@weft/utils';
import { useMenu } from 'apps/app/src/hooks/use-menu';
import React from 'react';
import type { ShareInvoiceData } from '../../../invoices-components/share-invoices-dialog/ShareInvoicesDialog';
import { ShareInvoicesDialog } from '../../../invoices-components/share-invoices-dialog/ShareInvoicesDialog';
import { BillingStatusMenu } from './BillingStatusMenu';

type InvoicesActionsMenuProps = {
  children?: React.ReactElement;
  invoices: InvoiceOverview[];
  onApproveInvoices?: () => void;
  onExportSelectedInvoices?: () => void;
  onUpdateInvoicesBillingStatus?: (status: InvoiceBillingStatus) => void
  onDownloadAsPdf?: () => void;
  onDownloadAsPdfStatement?: () => void;
  onSyncToStripe?: () => void;
  onSyncToNetSuite?: () => void;
  onMarkAsPaid?: () => void;
  connectedIntegrations: IntegrationProviders[];
  syncToNetSuiteDisabled?: boolean;
  disablePaidMark?: boolean;
};

export const InvoicesActionsMenu: React.FC<InvoicesActionsMenuProps> = ({
  invoices,
  onApproveInvoices,
  onExportSelectedInvoices,
  onUpdateInvoicesBillingStatus,
  onDownloadAsPdf,
  onDownloadAsPdfStatement,
  onSyncToStripe,
  onSyncToNetSuite,
  onMarkAsPaid,
  syncToNetSuiteDisabled,
  connectedIntegrations,
  disablePaidMark,
  children,
}) => {
  const { isOpen, handleMenuOpen, handleMenuClose, anchorElement } = useMenu();

  const handleMenuItemClick = (action?: () => void) => {
    return () => {
      handleMenuClose();
      action?.();
    };
  };

  return (
    <>
      {isNotNullOrUndefined(children) && React.cloneElement(children, {
        onClick: handleMenuOpen,
      })}
      <Menu
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={isOpen}
        anchorEl={anchorElement}
        onClose={handleMenuClose}
        MenuListProps={{
          className: 'py-2',
        }}
        slotProps={{
          paper: {
            className: 'mt-2 max-w-[250px] w-full rounded-lg',
          },
        }}
      >
        <MenuItem onClick={onApproveInvoices}>
          <Typography>Invoice approval</Typography>
        </MenuItem>

        {isNotNullOrUndefined(onUpdateInvoicesBillingStatus) && (
          <MenuItem className='pl-4 pr-0'>
            <BillingStatusMenu
              onClose={handleMenuClose}
              onUpdateInvoicesBillingStatus={(status) => onUpdateInvoicesBillingStatus?.(status)}
            >
              <Typography className='h-full w-full'>Change status</Typography>
            </BillingStatusMenu>
          </MenuItem>
        )}

        {!disablePaidMark && (
          <MenuItem
            className='pl-4'
            onClick={handleMenuItemClick(onMarkAsPaid)}
            disabled={isUnpaidInvoice(invoices)}
          >
            <Typography>Mark as paid</Typography>
          </MenuItem>
        )}

        {isNotNullOrUndefined(onDownloadAsPdf) && (
          <MenuItem
            className='pl-4'
            onClick={handleMenuItemClick(onDownloadAsPdf)}
          >
            <Typography>Download invoice</Typography>
          </MenuItem>
        )}

        {isNotNullOrUndefined(onDownloadAsPdfStatement) && (
          <MenuItem
            className='pl-4'
            onClick={handleMenuItemClick(onDownloadAsPdfStatement)}
          >
            <Typography>Export statement</Typography>
          </MenuItem>
        )}

        {connectedIntegrations.includes(INTEGRATION_PROVIDERS.stripe) && (
          <MenuItem
            className='pl-4'
            onClick={handleMenuItemClick(onSyncToStripe)}
          >
            <Typography>Sync to Stripe</Typography>
          </MenuItem>
        )}

        {connectedIntegrations.includes(INTEGRATION_PROVIDERS.netSuite) && (
          <MenuItem
            className='pl-4'
            onClick={handleMenuItemClick(onSyncToNetSuite)}
            disabled={syncToNetSuiteDisabled}
          >
            <Typography>Sync to NetSuite</Typography>
          </MenuItem>
        )}

        <ShareInvoicesDialog invoicesData={buildShareInvoicesData(invoices)}>
          <MenuItem disabled={areSomeInvoicesMissingEmail(invoices)}>
            <Typography>Share via email</Typography>
          </MenuItem>
        </ShareInvoicesDialog>

        <MenuItem onClick={onExportSelectedInvoices}>
          <Typography>Download as CSV</Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

function areSomeInvoicesMissingEmail(invoices: InvoiceOverview[]): boolean {
  return invoices.some((invoice) => isNullOrUndefined(invoice.customer.contacts) || isEmptyArray(invoice.customer.contacts));
}

function isUnpaidInvoice(invoices: InvoiceOverview[]): boolean {
  return invoices[0].billingStatus === INVOICE_BILLING_STATUSES.Paid;
}

function buildShareInvoicesData(invoiceOverviews: InvoiceOverview[]): ShareInvoiceData[] {
  return invoiceOverviews.map<ShareInvoiceData>((invoiceOverview) => ({
    amount: invoiceOverview.revenueBreakdown.total,
    currency: invoiceOverview.currency,
    customerName: invoiceOverview.customer.name,
    invoiceId: invoiceOverview.id,
    issueDate: invoiceOverview.billingCycle.startTime,
    contactEmail: isNotEmptyArray(invoiceOverview.customer.contacts) ? getPrimaryContact(invoiceOverview.customer.contacts).email : '---',
  }));
}